import { useState, useCallback, useEffect, useRef, type ReactNode, useMemo } from 'react';
import { constants } from '@/constants/constants';
import { ScrollContext } from './scrollContext';
import { useLocation } from 'react-router';

export const ScrollProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const isInnerPage = useMemo(() => {
    return location.pathname !== '/';
  }, [location]);

  const [isScrolled, setIsScrolled] = useState(isInnerPage);
  const [isMoving, setIsMoving] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const threshold = constants.SCROLL_THRESHOLD;

  const handleScroll = useCallback(() => {
    if (isInnerPage) return;

    const currentScroll = window.scrollY;
    setIsMoving(true);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsMoving(false), 150);

    setIsScrolled((prev) => {
      if (!prev && currentScroll > threshold) return true;
      if (prev && currentScroll < threshold - 80) return false;
      return prev;
    });
  }, [threshold, isInnerPage]);

  useEffect(() => {
    setIsScrolled(isInnerPage);
  }, [isInnerPage]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <ScrollContext.Provider value={{ isScrolled, isMoving, isInnerPage }}>
      {children}
    </ScrollContext.Provider>
  );
};
